import axios from "axios";

// Resolve the backend URL — `REACT_APP_BACKEND_URL` is baked into the build, but
// the user can browse the app under a different domain (e.g. a custom domain like
// `https://synergy-table.com` while the build embedded `https://synergy-table.emergent.host`).
// Calling cross-origin would break the httpOnly cookie auth (browsers drop third-party
// cookies). So if the current page origin differs from the baked URL, we route API
// calls through the current origin (same-origin) — assuming the same backend is
// reachable at `/api` on the user's domain, which is true for Emergent deploys + custom
// domains. Falls back gracefully when running in non-browser contexts (tests).
function resolveBackendUrl() {
  const baked = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined" || !window.location) return baked;
  const pageOrigin = window.location.origin;
  // Localhost / dev — always trust the env var.
  if (pageOrigin.startsWith("http://localhost") || pageOrigin.startsWith("http://127.")) {
    return baked;
  }
  try {
    const bakedOrigin = new URL(baked).origin;
    if (bakedOrigin === pageOrigin) return baked;
  } catch {
    // baked URL is empty or malformed — fall through to page origin
  }
  return pageOrigin;
}

const BACKEND_URL = resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

// Auth lives in an httpOnly cookie now — set by the backend on login.
// `withCredentials: true` makes every axios call carry that cookie.
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// On 401 from any endpoint (except /auth/me which is handled by AuthContext bootstrap),
// the cookie has expired or been revoked — surface it to the caller. The router-level
// ProtectedRoute will redirect to /login when AuthContext sees `user === false`.
api.interceptors.response.use(
  (r) => r,
  (e) => Promise.reject(e),
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
